import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
} from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/styles";
import ImageArchiveGrid from "./ImageArchiveGrid";
import { LayoutBodyContext } from "layout/LayoutBodyContext";
import {
  colors,
  margin,
  padding,
} from "theme/defaultStyle";
import PageSection from "controls/global/page-section";
import useDocumentStore from "utils/context/ImageArchiveContext";
import { ImageArchiveUI } from "entities/UIModel/ImageArchiveUI";
import { ImageArchiveSearchCriteria } from "entities/ApiModel/ImageArchiveSearchCriteria";
import { Order } from "utils/sorting";
import { useGlobalAccess } from "utils/context/GlobalAccessContext";
import { useUser } from "utils/context/UserContext";
import {
  orderBy as _orderBy,
  debounce
} from "lodash";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useMyFiles } from "utils/context/MyFilesContext";
import ImageArchiveHeader from "./ImageArchiveHeader";
import { DateTypeCode } from "utils/data/enum";
import ScrollToTopArrow from "controls/global/scroll-to-top-arrow";
import { IColumn } from "entities/ApiModel/IColumn";
import { ImageArchiveExportCriteria } from "entities/ApiModel/ImageArchiveExportCriteria";
import { getColumnSearchField } from "utils/columnsGrid";
const StyledBoxContainer = styled(Box)({
  position: "relative",
  "& .MuiOutlinedInput-input": {
    marginBottom: margin.xxsmall2,
    marginLeft: margin.small2,
    marginTop: margin.small,
    padding: `${padding.small} ${padding.small2}`,
  },
  "&.image-archive > div > div:nth-child(2):not(.MuiInputAdornment-root)": {
    boxShadow: "none",
    paddingTop: "10px",
  },
  "& div.MuiTableContainer-root": {
    marginTop: `-${margin.medium}`,
  },
  "& div.grid-edit-mode": {
    "& > div.MuiBox-root": {
      color: colors.grey11,
      "& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis)": {
        color: colors.grey11,
      },
      "& .MuiPaginationItem-root:not(.MuiPaginationItem-ellipsis).Mui-selected": {
        backgroundColor: colors.grey10,
      }
    }
  }
});


const ImageArchiveComponent = () => {
  const [initialLoad, setInitialLoad] = useState(true);
  const InitialMessage = "Loading results...";
  const NoRecordsMessage = "No results Found";
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ImageArchiveUI[]>();
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [, {
    getSearchDocuments,
    getInitialColumnsDefintion,
    getColumnsDefinition,
    setColumnDefinition,
    exportFiles,
  }] = useDocumentStore();
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [isAdvSearchOpen, setIsAdvSearchOpen] = useState<boolean>(false);
  const { generalConfig } = useConfigContext();

  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof ImageArchiveUI | undefined>("documentUploadedDate");
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [filters, setFilters] = useState<ImageArchiveSearchCriteria | undefined>(undefined);
  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;
  const globalAccessAgencyName = globalAccessAgency?.[0]?.CompanyName;
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const [defaultOpenedDate, setDefaultOpenedDate] = useState<Date>(new Date());
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);

  const [, { getRowsPerPageSetting, setRowsPerPageSetting }] = useUser();
  const [, { setRequestTimeoutMs }] = useMyFiles();
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([InitialMessage]);
  const [{ isEditMode }] = useDocumentStore();
  const [columns, setColumns] = useState<IColumn[]>(getInitialColumnsDefintion(false));
  const [hiddenColumns, setHiddenColumns] = useState<(keyof ImageArchiveUI)[]>(getInitialColumnsDefintion(true));
  const [exportedColumns, setExportedColumns] = useState<string[]>([]);
  const bodyRef = useContext(LayoutBodyContext);
  const getDatabaseSortColumnName = (name: string) => {
    const mappedColumnNames: { [key: string]: string } = {
      documentDisplayFileName: "DocumentDisplayFileName",
      companyName: "CompanyName",
      locationDisplayName: "LocationDisplayName",

      companyId: "CompanyId",
      documentUploadedByUserName: "DocumentUploadedByUserName",
      documentUploadedDate: "DocumentUploadedDate",
      rowsPerPage: "RowsPerPage",
      currentRow: "CurrentRow",
      currentPage: "CurrentPage",
      totalPages: "TotalPages",
      totalRows: "TotalRows",
      locationStateAbbr: "LocationStateAbbr",
      locationLegacyID: "LocationLegacyID",
      companyLegacyID: "CompanyLegacyID",
      documentSize: "DocumentSize",
      documentFileTypeCode: "DocumentFileTypeCode",
      clientFileID: "ClientFileID",
    };
    return mappedColumnNames[name] ?? name;
  };

  const getFileSearchCriteria = () => {
    let criteria: ImageArchiveSearchCriteria;

    criteria = {
      ...filters,
      listCount: rowsPerPage,
      requestedPage: page,
      dateTypeCode: isAdvSearchOpen ? DateTypeCode.UploadDate : undefined,
      ...(globalAccessAgencyID && { companyID: globalAccessAgencyID }),
    };
    if (sortColumn) {
      criteria = {
        ...criteria,
        sortColumn: sortColumn,
      };
    }

    return criteria;
  };

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const filterDays = generalConfig.filesSearchDefaultLookupDays;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;

    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (filterDays) {
      setDefaultOpenedDate(new Date(new Date().getTime() - filterDays * 24 * 60 * 60 * 1000));
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };

  const getDefaultFilters = (advanced: boolean): ImageArchiveSearchCriteria => {
    const sortColumn = advanced ? undefined : "-documentUploadedDate";
    const dateType = advanced ? DateTypeCode.UploadDate : undefined;

    const defaultFilters: ImageArchiveSearchCriteria = {
      listCount: rowsPerPage,
      dateTypeCode: dateType,
      requestedPage: 1,
      sortColumn: sortColumn,
    };
    return defaultFilters;
  };

  const performSearch = async () => {
    let criteria = getFileSearchCriteria();
    criteria.isInitialLoad = false;
    if (initialLoad) {
      criteria.isInitialLoad = true;
      setInitialLoad(false);
    }
    let files: ImageArchiveUI[] | undefined = [];
    try {
      files = await getSearchDocuments({ ...criteria });
      if (files !== undefined) {
        setData(files);
        if (!files?.length) {
          setNoRecordsMessage(getNoRecordsMessage());
          setPage(1);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleRowsPerPageChange = async (count: number) => {
    setRowsPerPageSetting(count);
    setRowsPerPage(count);
    setPartialFilterUpdate(false);
    setPage(1);
  };

  const getNoRecordsMessage = () => {
    if (!filters) {
      return [NoRecordsMessage];
    }
    let firstMessage = "No Matches Found";
    const secondMessage = "Please adjust your search criteria and search again.";
    const keys: (keyof ImageArchiveSearchCriteria)[] = [
      "documentDisplayFileName",
      "companyName",
      "locationDisplayName",
      "documentUploadedByUserName",
      "companyID",
      "locationDisplayName",
      "clientFileID",
      "documentFileTypeCode",
      "documentSize",
      "companyLegacyID",
      "locationLegacyID",
      "locationStateAbbr",
    ];
    let providedFields = keys.map((key) => filters[key]).filter((value) => !!value && value !== globalAccessAgencyID);

    if (!filters.companyLegacyID && globalAccessAgencyName && !providedFields.includes(globalAccessAgencyName)) {
      providedFields.push(globalAccessAgencyName);
    }

    if (providedFields.length) {
      firstMessage = `No matches found for "${providedFields.join(", ")}"`;
    }
    return [firstMessage, secondMessage];
  };

  const handleAdvancedFilterChange = (
    name: keyof ImageArchiveSearchCriteria,
    value: any,
  ) => {
    if (isAdvSearchOpen) {
      handleFilterChange(name, value);
    }
  };

  const handleAdvanceSearchPanelOpen = async (open: boolean) => {

    const resetFilters = (filters: ImageArchiveSearchCriteria | undefined) => {
      return getDefaultFilters(open);
    };
    setFiltersApplied(false);
    setFilters(resetFilters);
    setSortColumn(undefined);
    setPartialFilterUpdate(false);
    setIsAdvSearchOpen(open);
  };

  const handlePageChange = async (page: number) => {
    setPage(page);
  };

  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
  }, [readyToSearch]);

  const canSearch = (): boolean => {
    return !partialFilterUpdate && filters !== undefined && Object.values(filters).length > 0 && !isEditMode;
  };

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs),
    [userInputDebounceTimeMs],
  );

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [
    filters,
    page,
    sortColumn,
    filtersApplied,
    settingsLoaded,
    columns,
    rowsPerPage,
    partialFilterUpdate,
    globalAccessAgencyName,
  ]);

  useEffect(() => {
    const defaultFilters = getDefaultFilters(false);
    setFilters(defaultFilters);
  }, [defaultOpenedDate]);

  
  const getSearchField = (field: keyof ImageArchiveUI) =>
    getColumnSearchField<ImageArchiveUI, ImageArchiveSearchCriteria>(columns, field);

  const handleRequestSort = async (columnName: any, disableToggling: boolean = false) => {
    let sortBy = getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof ImageArchiveUI);
    setSortColumn(sortBy);
  };

  const handleFilterChange = async (name: keyof ImageArchiveSearchCriteria, value: any) => {
    const formattedValue = typeof value === "string" ? value.trim() : value;
    const updatedFilters = (filters: ImageArchiveSearchCriteria | undefined) => {
      let dateType;
      if (name === "dateTypeCode") {
        dateType = value;
      } else if (name === "documentUploadedDate") {
        handleRequestSort("documentUploadedDate", true);
      } else {
        dateType = filters?.dateTypeCode;
      }
      let adjustedFilters: ImageArchiveSearchCriteria = {
        ...filters,
        [name]: formattedValue,
        dateTypeCode: dateType,
      };
      adjustedFilters = removeInvalidProperties(adjustedFilters).object;
      return adjustedFilters;
    };

    setFiltersApplied(true);
    setFilters(updatedFilters);
  };
  useEffect(() => {
    bodyRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [rowsPerPage]);
  useEffect(() => {
    const getCount = async () => {
      const count = await getRowsPerPageSetting();
      setRowsPerPage(count);
    };

    getCount();
  }, [getRowsPerPageSetting]);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      setSettingsLoaded(true);
    };

    init();
  }, []);
  useEffect(() => {
    const getColumns = async () => {
      const { gridColumns, gridHiddenColumns } = await getColumnsDefinition();
      setColumns(gridColumns);
      setHiddenColumns(gridHiddenColumns);
    };
    getColumns();
  }, [getColumnsDefinition]);

  useEffect(() => {
    let sortColumnChanged = false;
    if (sortColumn) {
      const sortColumnName = sortColumn.replace("-", "");
      hiddenColumns.forEach((c) => {
        const mappedName = getDatabaseSortColumnName(c);
        if (mappedName === sortColumnName) {
          setSortColumn(undefined);
          sortColumnChanged = true;
          return;
        }
      });
    }

    const updatedFilters = removeInvalidProperties({ ...filters });
    if (sortColumnChanged || updatedFilters.deleteCount || data === undefined) {
      setFilters((filters: ImageArchiveSearchCriteria | undefined) => updatedFilters.object);
    }
  }, [hiddenColumns]);

  useEffect(() => {
    handleColumnsModified(columns);
  }, [hiddenColumns]);

  useEffect(() => {
    const exportedCols = _orderBy(columns.filter(c => !hiddenColumns.includes(c.field as keyof ImageArchiveUI)), "position")
      .map(c => c.field);

    setExportedColumns(exportedCols);
  }, [columns]);

  useEffect(() => {
    setInitialLoad(true);
  },[])

  const handleColumnResize = (column: keyof ImageArchiveUI, newWidth: number) => {
    if (column && newWidth > 0) {
      setColumnDefinition([column], "width", Math.floor(newWidth));
    }
  };
  const handleUpdateHiddenColumns = (columns: (keyof ImageArchiveUI)[]) => {
    setHiddenColumns(columns);
  };

  const handleExport = async () => {
    let searchCriteria: ImageArchiveSearchCriteria = {
      ...getFileSearchCriteria(),
    };
    searchCriteria.listCount = 0; // to export all the rows
    const exportCriteria: ImageArchiveExportCriteria = {
      DocumentIds: [],
      ExportColumns: exportedColumns,
      SearchCriteria: searchCriteria
    };
    await exportFiles(exportCriteria);
  };

  const handleColumnsModified = (columns: IColumn[]) => {
    const exportedCols = columns.filter(c => !hiddenColumns.includes(c.field as keyof ImageArchiveUI))
      .map(c => c.field);
    setExportedColumns(exportedCols);
  };

  const visibleColumns = columns
    .filter((c) => c.hideable)
    .map((c) => ({
      field: c.field as keyof ImageArchiveUI,
      name: c.name,
      checked: !hiddenColumns.includes(c.field as keyof ImageArchiveUI),
    }));
    
  const removeInvalidProperties = (object: { [key: string]: any; }) => {
    let deleteCount = 0;
    Object.keys(object).forEach((key) => {

      // Remove null properties and fields that are hidden,
      // with the exception of dateRangeStart which can always be searched even if hidden.
      if (object[key] === null ||
        object[key] === undefined ||
        (!isAdvSearchOpen
          && key !== "dateRangeStart"
          && hiddenColumns.find(c => c === key || getSearchField(c) === key)
        )
      ) {
        delete object[key];
        deleteCount++;

      }
    });

    return { object, deleteCount };
  };
  return (
    <>
      <StyledBoxContainer className="image-archive">
        <PageSection title="">
          <>
            <Typography className="subheading">Search below for policy images uploaded by your agency.</Typography>
            <ImageArchiveHeader
              columns={visibleColumns}
              hiddenColumns={hiddenColumns}
              updateHiddenColumns={handleUpdateHiddenColumns}
              handleAdvancedFilterChange={handleAdvancedFilterChange}
              handleAdvanceSearchPanelOpen={handleAdvanceSearchPanelOpen}
              onExport={handleExport}
            />
            <ImageArchiveGrid
              performSearch={performSearch}
              colsConfig={columns}
              rows={data ?? []}
              rowsPerPage={rowsPerPage}
              noRowsMessage={noRecordsMessage}
              page={page}
              hiddenColumns={hiddenColumns}
              onFiltersChange={handleFilterChange}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onColumnResize={handleColumnResize}
            />
            <ScrollToTopArrow refreshSize={data} />
          </>
        </PageSection>
      </StyledBoxContainer>
    </>
  );
};

export default ImageArchiveComponent;
